import { Badge } from '@mantine/core';
import React from 'react';
import {
  Archive,
  Basket,
  BuildingStore,
  Coin,
  Home,
  ListCheck,
  ReportAnalytics,
  Settings,
  Shirt,
  ShirtSport,
  TruckDelivery,
  User
} from 'tabler-icons-react';
import { EXTERNAL_CONNECTION_CONFIGS } from '../external/config/externalConnectionConfigs';

const BASE_NAVIGATION_LINKS = {
  public: [],
  profile: [],
  admin: [
    {
      to: '/admin',
      title: 'Admin Dashboard',
      icon: User,
      isSelected: (pathname) => pathname === '/admin',
      hasAccess: (authState) => authState.userData?.isAdmin
    },
    {
      to: '/merchant',
      linkOverride: (authState) =>
        !authState.pkEcomStore ? '/merchant/stores/switch' : '/merchant',
      title: 'Store Dashboard',
      icon: BuildingStore,
      isSelected: (pathname) => pathname === '/merchant',
      hasAccess: (authState) => true
    },
    {
      to: '/vendor',
      title: 'Vendor Dashboard',
      icon: TruckDelivery,
      isSelected: (pathname) => pathname === '/vendor',
      hasAccess: (authState) =>
        authState.ecomVendors.length > 0 || authState.userData?.isAdmin
    }
  ]
};

const VENDOR_NAVIGATION_LINKS = {
  main: [
    {
      to: '/vendor/setup-guide',
      title: 'Setup guide',
      icon: ListCheck,
      isSelected: (pathname, state, authState) => {
        const ecomVendor = authState.ecomVendors.find(
          (s) => s.pkEcomVendor.toString() === authState.pkEcomVendor.toString()
        );

        if (!ecomVendor || ecomVendor?.sandboxEnabled) {
          return (
            pathname.startsWith('/vendor/setup-guide') ||
            pathname === '/vendor' ||
            pathname === '/vendor/'
          );
        }
        return pathname.startsWith('/vendor/setup-guide');
      }
    },
    {
      to: '/vendor/products',
      title: 'Products',
      icon: Shirt,
      isSelected: (pathname) => pathname.startsWith(`/vendor/products`),
      links: [
        {
          to: '/vendor/product-bundles',
          title: 'Bundles',
          isSelected: (pathname) =>
            pathname.startsWith(`/vendor/product-bundles`)
        },
        {
          to: '/vendor/collections',
          title: 'Collections',
          isSelected: (pathname) => pathname.startsWith(`/vendor/collections`)
        },
        {
          to: '/vendor/designs',
          title: 'Designs',
          isVisible: (vendorState, authState) =>
            vendorState.ecomVendorSetupSummary.value?.ecomVendor.ecomVendorExternalConnections.some(
              (c) =>
                EXTERNAL_CONNECTION_CONFIGS.find(
                  (f) =>
                    f.pkEcomExternalConnectionType.toString() ===
                    c.fkEcomExternalConnectionType.toString()
                )?.products?.restrictions?.canCustomizeDesign
            ),
          isSelected: (pathname) => pathname.startsWith(`/vendor/designs`)
        },
        {
          to: '/vendor/inventory',
          title: 'Inventory',
          isSelected: (pathname) => pathname.startsWith(`/vendor/inventory`)
        }
      ]
    },
    {
      to: '/vendor/orders',
      title: 'Orders',
      icon: Basket,
      isSelected: (pathname) => pathname.startsWith('/vendor/orders'),
      getRightSection: (state, pathname) => {
        const navigationOpen =
          pathname.startsWith('/vendor/orders') ||
          pathname.startsWith(`/vendor/bulk-orders`) ||
          pathname.startsWith(`/vendor/customers`) ||
          pathname.startsWith(`/vendor/merchants`);

        const { unfulfilledIndividualOrderCount, unfulfilledBulkOrderCount } =
          state.ecomVendorSetupSummary.value || {};

        const ordercount = state.ecomVendorSetupSummary.value
          ? navigationOpen
            ? unfulfilledIndividualOrderCount
            : unfulfilledIndividualOrderCount + unfulfilledBulkOrderCount
          : 0;

        return ordercount > 0 && <Badge color="orange">{ordercount}</Badge>;
      },
      links: [
        {
          to: '/vendor/bulk-orders',
          title: 'Bulk orders',
          isSelected: (pathname) => pathname.startsWith(`/vendor/bulk-orders`),
          getRightSection: (state) =>
            state.ecomVendorSetupSummary.value?.unfulfilledBulkOrderCount >
              0 && (
              <Badge color="orange">
                {state.ecomVendorSetupSummary.value?.unfulfilledBulkOrderCount}
              </Badge>
            )
        },
        {
          to: '/vendor/customers',
          title: 'Customers',
          isSelected: (pathname) => pathname.startsWith(`/vendor/customers`)
        },
        {
          to: '/vendor/merchants',
          title: 'Merchants',
          isSelected: (pathname) => pathname.startsWith(`/vendor/merchants`)
        }
      ]
    },
    {
      to: '/vendor/analytics',
      title: 'Analytics',
      icon: ReportAnalytics,
      isSelected: (pathname, state, authState) => {
        const ecomVendor = authState.ecomVendors.find(
          (s) => s.pkEcomVendor.toString() === authState.pkEcomVendor.toString()
        );

        if (!ecomVendor || ecomVendor?.sandboxEnabled) {
          return pathname.startsWith('/vendor/analytics');
        }
        return (
          pathname.startsWith('/vendor/analytics') ||
          pathname === '/vendor' ||
          pathname === '/vendor/'
        );
      },
      links: [
        {
          to: '/vendor/reports',
          title: 'Reports',
          isSelected: (pathname) => pathname.startsWith('/vendor/reports')
        }
      ]
    },
    {
      to: '/vendor/settings',
      title: 'Settings',
      icon: Settings,
      isSelected: (pathname) => pathname.startsWith('/vendor/settings')
    }
  ]
};

const MERCHANT_NAVIGATION_LINKS = {
  main: [
    {
      to: '/merchant/setup-guide',
      title: 'Setup guide',
      icon: ListCheck,
      isSelected: (pathname, state, authState) => {
        const ecomStore = authState.ecomStores.find(
          (s) => s.pkEcomStore.toString() === authState.pkEcomStore.toString()
        );

        if (!ecomStore || ecomStore?.sandboxEnabled) {
          return (
            pathname.startsWith('/merchant/setup-guide') ||
            pathname === '/merchant' ||
            pathname === '/merchant/'
          );
        }
        return pathname.startsWith('/merchant/setup-guide');
      }
    },
    {
      to: '/merchant/products',
      title: 'Products',
      icon: Shirt,
      isSelected: (pathname) => pathname.startsWith('/merchant/products'),
      links: [
        {
          to: '/merchant/product-bundles',
          title: 'Bundles',
          isSelected: (pathname) =>
            pathname.startsWith(`/merchant/product-bundles`)
        },
        {
          to: '/merchant/collections',
          title: 'Collections',
          isSelected: (pathname) => pathname.startsWith(`/merchant/collections`)
        }
      ]
    },
    {
      to: '/merchant/fundraising',
      title: 'Fundraising',
      icon: Coin,
      isSelected: (pathname) => pathname.startsWith('/merchant/fundraising')
    },
    {
      to: '/merchant/orders',
      title: 'Orders',
      icon: Basket,
      isSelected: (pathname) => pathname.startsWith('/merchant/orders'),
      getRightSection: (state, pathname) => {
        const navigationOpen =
          pathname.startsWith('/merchant/orders') ||
          pathname.startsWith(`/merchant/group-orders`) ||
          pathname.startsWith(`/merchant/customers`);

        const { unfulfilledIndividualOrderCount, unfulfilledBulkOrderCount } =
          state.ecomStoreSetupSummary.value || {};

        const ordercount = state.ecomStoreSetupSummary.value
          ? navigationOpen
            ? unfulfilledIndividualOrderCount
            : unfulfilledIndividualOrderCount + unfulfilledBulkOrderCount
          : 0;
        return ordercount > 0 && <Badge color="orange">{ordercount}</Badge>;
      },
      links: [
        {
          to: '/merchant/group-orders',
          title: 'Group orders',
          isSelected: (pathname) =>
            pathname.startsWith(`/merchant/group-orders`),
          getRightSection: (state) =>
            state.ecomStoreSetupSummary.value?.unfulfilledBulkOrderCount >
              0 && (
              <Badge color="orange">
                {state.ecomStoreSetupSummary.value?.unfulfilledBulkOrderCount}
              </Badge>
            )
        },
        {
          to: '/merchant/customers',
          title: 'Customers',
          isSelected: (pathname) => pathname.startsWith(`/merchant/customers`)
        }
      ]
    },
    {
      to: '/merchant/analytics',
      title: 'Analytics',
      icon: ReportAnalytics,
      isSelected: (pathname, state, authState) => {
        const ecomStore = authState.ecomStores.find(
          (s) => s.pkEcomStore.toString() === authState.pkEcomStore.toString()
        );

        if (!ecomStore || ecomStore?.sandboxEnabled) {
          return pathname.startsWith('/merchant/analytics');
        }
        return (
          pathname.startsWith('/merchant/analytics') ||
          pathname === '/merchant' ||
          pathname === '/merchant/'
        );
      },
      links: [
        {
          to: '/merchant/reports',
          title: 'Reports',
          isSelected: (pathname) => pathname.startsWith('/merchant/reports')
        }
      ]
    },
    {
      to: '/merchant/settings',
      title: 'Settings',
      icon: Settings,
      isSelected: (pathname) => pathname.startsWith('/merchant/settings')
    }
  ],
  sections: [
    {
      title: 'Store',
      links: [
        {
          to: '/merchant/webstore/themes',
          title: 'Online Store',
          icon: BuildingStore,
          isSelected: (pathname) => false,
          links: [
            {
              to: '/merchant/webstore/themes',
              title: 'Themes',
              isSelected: (pathname) =>
                pathname.startsWith(`/merchant/webstore/themes`)
            },
            // {
            //   to: '/merchant/webstore/pages',
            //   title: 'Pages',
            //   isSelected: (pathname) =>
            //     pathname.startsWith(`/merchant/webstore/pages`)
            // },
            {
              to: '/merchant/webstore/menus',
              title: 'Navigation',
              isSelected: (pathname) =>
                pathname.startsWith(`/merchant/webstore/menus`)
            }
          ]
        }
      ]
    }
  ]
};

const ADMIN_NAVIGATION_LINKS = {
  main: [
    {
      to: '/admin',
      title: 'Analytics',
      icon: ReportAnalytics,
      isSelected: (pathname) => pathname === '/admin' || pathname === '/admin/',
      links: [
        {
          to: '/admin/reports',
          title: 'Reports',
          isSelected: (pathname) => pathname.startsWith('/admin/reports')
        }
      ]
    },
    {
      to: '/admin/products',
      title: 'External Products',
      icon: ShirtSport,
      isSelected: (pathname) => pathname.startsWith('/admin/products')
    },
    {
      to: '/admin/stock-content',
      title: 'Stock Content',
      icon: Archive,
      isSelected: (pathname) => pathname.startsWith('/admin/stock-content')
    },
    {
      to: '/admin/orders',
      title: 'Orders',
      icon: Basket,
      isSelected: (pathname) => pathname.startsWith('/admin/orders'),
      links: [
        {
          to: '/admin/customers',
          title: 'Customers',
          isSelected: (pathname) => pathname.startsWith(`/admin/customers`)
        }
      ]
    },
    {
      to: '/admin/stores',
      title: 'Stores',
      icon: BuildingStore,
      isSelected: (pathname) => pathname.startsWith('/admin/stores')
    },
    {
      to: '/admin/vendors',
      title: 'Vendors',
      icon: TruckDelivery,
      isSelected: (pathname) => pathname.startsWith('/admin/vendors')
    }
  ]
};

export {
  BASE_NAVIGATION_LINKS,
  VENDOR_NAVIGATION_LINKS,
  MERCHANT_NAVIGATION_LINKS,
  ADMIN_NAVIGATION_LINKS
};
